import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { HolidayFormData } from '../common/types';
import { getAllZones } from '../../api/master';
import { useAuth } from '../../context/AuthContext';
import Holiday from '../../types/holiday';

interface Zone {
  id: number;
  name: string;
}

interface HolidayFormModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: HolidayFormData) => void;
  title: string;
  submitButtonText: string;
  type: 'public' | 'restricted';
  initialData?: Holiday | null;
}


const HolidayFormModal: React.FC<HolidayFormModalProps> = ({
  isOpen,
  onOpenChange,
  onSubmit,
  title,
  submitButtonText,
  type,
  initialData,
}) => {
  const { user } = useAuth();

  const [formData, setFormData] = useState<HolidayFormData>({
    zone_id: 0,
    holiday_date: '',
    name: ''
  });

  const [zones, setZones] = useState<Zone[]>([]);
  const [loadingZones, setLoadingZones] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        zone_id: initialData.zone_id,
        holiday_date: initialData.holiday_date,
        name: initialData.name,
      });
    }
  }, [initialData]);

  useEffect(() => {
    if (!user || !isOpen) return;

    const fetchZones = async () => {
      try {
        setLoadingZones(true);
        const response = await getAllZones(user.tenant_id);
        if (response) setZones(response);
      } catch (error: any) {
        console.error('Failed to fetch zones:', error.message);
      } finally {
        setLoadingZones(false);
      }
    };

    fetchZones();
  }, [user, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    onSubmit({
      ...formData,
    });

    setFormData({
      zone_id: 0,
      holiday_date: '',
      name: ''
    });

    onOpenChange(false);
  };

  const handleInputChange = <K extends keyof HolidayFormData>(
    field: K,
    value: HolidayFormData[K]
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const isSubmitDisabled =
    !formData.zone_id ||
    !formData.holiday_date ||
    !formData.name.trim();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-2xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={() => onOpenChange(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Zone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Zone *
            </label>
            <select
              value={formData.zone_id}
              onChange={(e) =>
                handleInputChange('zone_id', Number(e.target.value))
              }
              required
              disabled={loadingZones}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
            >
              <option value={0}>
                {loadingZones ? 'Loading zones…' : 'Select Zone'}
              </option>

              {zones.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.name}
                </option>
              ))}
            </select>
          </div>

          {/* Holiday Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Holiday Date *
            </label>
            <input
              type="date"
              value={formData.holiday_date}
              onChange={(e) =>
                handleInputChange('holiday_date', e.target.value)
              }
              required
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
            />
          </div>

          {/* Holiday Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Holiday Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter holiday name"
              required
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-4 pt-4 border-t">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-6 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSubmitDisabled}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitButtonText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HolidayFormModal;
